import React, { useEffect, useState } from 'react'
import { adminApi } from '@/services/adminApi'
import { Button } from '@/components/ui/button'
import { Save } from 'lucide-react'
import { toast } from 'sonner'
import type { RateLimitConfig } from '@/types/admin'

type TierKey = 'strictLimit' | 'moderateLimit' | 'standardLimit' | 'relaxedLimit' | 'publicLimit'

const TIERS: {
  key: TierKey
  label: string
  dot: string
  labelColor: string
  routes: string[]
  note?: string
}[] = [
  {
    key: 'strictLimit',
    label: 'STRICT',
    dot: '#ef4444',
    labelColor: 'text-red-600',
    routes: [
      '/chat/query', '/chat/rewrite-prompt', '/chat/inject',
      '/api/bi/chat', '/api/code-review/chat',
      '/api/email-assistant/*', '/api/meeting-preparer/*', '/api/tasks',
    ],
  },
  {
    key: 'moderateLimit',
    label: 'MODERATE',
    dot: '#f97316',
    labelColor: 'text-orange-600',
    routes: ['/search', '/connectors', '/workflows', '/contacts', '/api/tools', '/api/slides', '/api/recall', '/api/github'],
  },
  {
    key: 'standardLimit',
    label: 'STANDARD',
    dot: '#eab308',
    labelColor: 'text-yellow-600',
    routes: ['/upload', '/memories', '/organization', '/groups', '/teams', '/share', '/dashboard', '/bi', '/bi/charts', '/feedback', '/evaluation', '/library'],
  },
  {
    key: 'relaxedLimit',
    label: 'RELAXED',
    dot: '#22c55e',
    labelColor: 'text-green-600',
    routes: ['/feature-flags', '/query-state', '/versioning', '/billing', '/onboarding', '/agent-config', '/config', '/admin', '/api/transcribe', '/api/issues', '/logs'],
  },
  {
    key: 'publicLimit',
    label: 'PUBLIC',
    dot: '#3b82f6',
    labelColor: 'text-blue-600',
    routes: ['/auth', '/public', '/files'],
    note: 'per IP',
  },
]

const NONE_ROUTES = ['GET /chat/*', '/webhook', '/health', '/admin', '/api/libra']

const DEFAULT_CONFIG: RateLimitConfig = {
  strictLimit: 30,
  moderateLimit: 60,
  standardLimit: 120,
  relaxedLimit: 240,
  publicLimit: 30,
  windowSeconds: 60,
  updatedAt: '',
}

export default function RateLimitSettingsPage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState<RateLimitConfig>(DEFAULT_CONFIG)

  useEffect(() => {
    adminApi.getRateLimitConfig()
      .then(setConfig)
      .catch((e: any) => toast.error(e?.response?.data?.error || e?.message || 'Failed to load config'))
      .finally(() => setLoading(false))
  }, [])

  const handleChange = (field: keyof Omit<RateLimitConfig, 'updatedAt'>, value: string) => {
    const num = parseInt(value, 10)
    if (!isNaN(num)) setConfig(prev => ({ ...prev, [field]: num }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const { updatedAt: _ts, ...payload } = config
      const updated = await adminApi.updateRateLimitConfig(payload)
      setConfig(updated)
      toast.success('Rate limit config saved')
    } catch (e: any) {
      toast.error(e?.response?.data?.error || e?.message || 'Failed to save config')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-gray-900">Rate Limit Config</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            Limits are cached in Redis for 5 min — saves invalidate immediately
            {config.updatedAt && ` · updated ${new Date(config.updatedAt).toLocaleString()}`}
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} size="sm" className="flex items-center gap-1.5 shrink-0">
          <Save className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>

      {/* Window row */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span className="font-medium text-gray-700">Window</span>
        <input
          type="number"
          min={10}
          value={config.windowSeconds}
          onChange={e => handleChange('windowSeconds', e.target.value)}
          className="w-16 px-2 py-1 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
        <span>seconds — shared across all tiers · min 10s</span>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden text-sm">

        {/* Head */}
        <div className="grid grid-cols-[140px_180px_1fr] bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-400 uppercase tracking-wider">
          <div className="px-4 py-2.5">Tier</div>
          <div className="px-4 py-2.5 border-l border-gray-200">Limit</div>
          <div className="px-4 py-2.5 border-l border-gray-200">Routes sharing this bucket</div>
        </div>

        {/* Tier rows */}
        {TIERS.map(tier => (
          <div key={tier.key} className="grid grid-cols-[140px_180px_1fr] border-b border-gray-100 hover:bg-gray-50/50 transition-colors">

            {/* Tier */}
            <div className="flex items-center gap-2.5 px-4 py-3">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: tier.dot }}
              />
              <span className={`text-xs font-bold tracking-wide ${tier.labelColor}`}>{tier.label}</span>
            </div>

            {/* Limit */}
            <div className="flex items-baseline gap-1.5 px-4 py-3 border-l border-gray-100">
              <input
                type="number"
                min={1}
                value={config[tier.key]}
                onChange={e => handleChange(tier.key, e.target.value)}
                className="w-14 px-2 py-1 text-sm text-center border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-xs text-gray-400 whitespace-nowrap">
                req / {config.windowSeconds}s
                {tier.note && <span className="ml-1 text-gray-300">· {tier.note}</span>}
              </span>
            </div>

            {/* Routes */}
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 px-4 py-3 border-l border-gray-100">
              {tier.routes.map(route => (
                <span key={route} className="text-xs font-mono text-gray-500">
                  {route}
                </span>
              ))}
            </div>
          </div>
        ))}

        {/* None row */}
        <div className="grid grid-cols-[140px_180px_1fr] text-gray-400">
          <div className="flex items-center gap-2.5 px-4 py-3">
            <span className="inline-block w-2 h-2 rounded-full bg-gray-300 shrink-0" />
            <span className="text-xs font-bold tracking-wide">NONE</span>
          </div>
          <div className="flex items-center px-4 py-3 border-l border-gray-100">
            <span className="text-xs italic">unlimited</span>
          </div>
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 px-4 py-3 border-l border-gray-100">
            {NONE_ROUTES.map(route => (
              <span key={route} className="text-xs font-mono text-gray-300">{route}</span>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}
